import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechHttpSubmitEntityRequest, useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { SaleInvoiceModifier } from "./SaleInvoiceModifierData";
import { useAgTechQuery } from "@agtech/core/data/queries/AgTechDataQueries";
import { InvoiceModifierDataGetAllInvoiceModifiersQuery } from "./SaleInvoiceModifierDataQueries";
import { AgTechIndex, Index } from "@agtech/core/utilities/AgTechUtilities";
import { useAgTechPopUpContext } from "@agtech/core/functions/AgTechAppPopUps";

/// DTOs

export type SaleInvoiceModifierWriteDTO = {
    InvoiceModifier: SaleInvoiceModifier
}

export const useSaleInvoiceModifierDataWriteInvoiceModifierAction = (): AgTechDataAction<SaleInvoiceModifierWriteDTO, SaleInvoiceModifier, AgTechIndex<SaleInvoiceModifier>> => {
    const InvoiceModifiersQuery = useAgTechQuery({
        query: InvoiceModifierDataGetAllInvoiceModifiersQuery
    });

    const saleInvoiceModifierSubmitRequest = useAgTechHttpSubmitEntityRequest<SaleInvoiceModifier>();

    return {
        name: 'WriteInvoiceModifier',
        query: InvoiceModifiersQuery,
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity ? 'Updating charge or discount...' : 'Creating charge or discount...',
            actionConfirmationMessage: `Successfully ${props.originalEntity ? 'updated' : 'created' } charge or discount`
        }),
        validate: async props  => {
            if (props.submittedEntity.InvoiceModifier.amount <= 0) {
                props.validation.failWithWarning("Please specify a valid charge or discount amount");
            }

            if (props.submittedEntity.InvoiceModifier.isPercentage && props.submittedEntity.InvoiceModifier.amount > 100) {
                props.validation.failWithWarning(props.submittedEntity.InvoiceModifier.amount + '% is not a valid percentage');
            }

            const existingInvoiceModifiers = Object.values(props.context);

            const existingInvoiceModifierWithSameName = existingInvoiceModifiers.filter(disc =>
                disc.name === props.submittedEntity.InvoiceModifier.name &&
                disc.id !== props.submittedEntity.InvoiceModifier.id);

            if (existingInvoiceModifierWithSameName && existingInvoiceModifierWithSameName.length > 0) {
                props.validation.failWithWarning(`A charge or discount with name "${props.submittedEntity.InvoiceModifier.name}" already exists. Please choose a different name.`);
            }
        },
        action: async props => {
            return await saleInvoiceModifierSubmitRequest.post({
                path: 'InvoiceModifiers',
                postedData: props.submittedEntity.InvoiceModifier,
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: InvoiceModifierDataGetAllInvoiceModifiersQuery,
                update: InvoiceModifiers => {
                    return {
                        ...InvoiceModifiers,
                        [props.responseData.id]: props.responseData
                    };
                }
            });
        }
    }
}

export const useSaleInvoiceModifierDataDeleteInvoiceModifierAction = (): AgTechDataAction<SaleInvoiceModifier, boolean> => {
    const popupContext = useAgTechPopUpContext();
    const InvoiceModifierDeletionRequest = useAgTechHttpPostRequest<SaleInvoiceModifier, boolean>();

    return {
        name: 'DeleteInvoiceModifier',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting charge or discount...',
            actionConfirmationMessage: `Successfully deleted charge or discount`
        }),
        confirm: async props => {
            return await props.appContext.confirmations.askForConfirmation({
                content: {
                    header: `Are you sure you want to delete charge or discount "${props.submittedEntity.name}" from the system?`,
                    details: "This charge or discount will no longer be available when creating invoices, but existing invoices with this charge or discount will not be changed",
                    width: '500px',
                    confirmationButtonText: 'Delete charge or discount'
                },
                submission: {
                    actionExecutionMessage: 'Deleting charge or discount...',
                    actionConfirmationMessage: 'charge or discount deleted'
                }
            })
        },
        action: async props => {
            return await InvoiceModifierDeletionRequest.post({
                path: 'InvoiceModifiers/Delete',
                postedData: props.submittedEntity,
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: InvoiceModifierDataGetAllInvoiceModifiersQuery,
                update: InvoiceModifiers => {
                    const updatedInvoiceModifiers = Object.values(InvoiceModifiers)
                        .filter(e => e.id !== props.submittedEntity.id);

                    return Index(updatedInvoiceModifiers, e => e.id);
                }
            });

            popupContext.closePopUp();
        }
    }
}